import { useState } from 'react';
import { useQuery, useQueryClient } from 'react-query';
import { Alert, Box, Button, MenuItem, Stack, TextField, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { client } from '@frontend/features/common/services';
import { useAuth } from '@frontend/features/auth/hooks/useAuth';

type Policy = { defaultBasisPoints: number; overrides: { agencyId: number; basisPoints: number }[] };
type Settings = { revision: number; policy: Policy; agencies: { id: number; name: string }[] };
export function useSuperAdmin() {
  const { myProfile } = useAuth();
  return useQuery(
    ['super-admin', myProfile?.id],
    async () => (await client.get<{ allowed: boolean }>('super-admin/capability')).data.allowed,
    { enabled: !!myProfile?.id, retry: false, staleTime: 0 },
  );
}
export function HotelCostMarkup({ agencyId }: { agencyId: number }) {
  const { myProfile } = useAuth();
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const path = `agencies/${agencyId}/hotels/cost-markup`;
  const settings = useQuery(
    ['hotel-cost-markup', agencyId, myProfile?.id],
    async () => (await client.get<Settings>(path)).data,
    {
      retry: false,
    },
  );
  const admins = useQuery(
    ['super-admin-users', myProfile?.id],
    async () => (await client.get<{ data: { id: string; email: string }[] }>('super-admin/users')).data.data,
    { retry: false },
  );
  const [draft, setDraft] = useState<Settings>();
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(false);
  const [saved, setSaved] = useState(false);
  const value = draft ?? settings.data;
  const run = async (work: () => Promise<unknown>) => {
    setBusy(true);
    setError(false);
    setSaved(false);
    try {
      await work();
      setSaved(true);
    } catch {
      setError(true);
    } finally {
      setBusy(false);
    }
  };
  const role = (target: string, enabled: boolean) =>
    run(async () => {
      await client.put('super-admin/users', { email: target, enabled });
      setEmail('');
      await queryClient.invalidateQueries(['super-admin-users']);
      await queryClient.invalidateQueries(['super-admin']);
    });
  const update = (policy: Policy) => {
    if (value) setDraft({ ...value, policy });
    setSaved(false);
  };
  const percentage = (raw: string) => (raw.trim() === '' ? NaN : Math.round(Number(raw) * 100));
  const valid =
    value &&
    [value.policy.defaultBasisPoints, ...value.policy.overrides.map((x) => x.basisPoints)].every(
      (x) => Number.isInteger(x) && x >= 0 && x < 10000,
    ) &&
    new Set(value.policy.overrides.map((x) => x.agencyId)).size === value.policy.overrides.length;
  if (!value)
    return (
      <Alert severity={settings.isError ? 'error' : 'info'}>
        {t(settings.isError ? 'hotelCostMarkup.error' : 'hotelConnections.loading')}
      </Alert>
    );
  return (
    <Stack spacing={2} sx={{ '& input, & .MuiSelect-select': { fontSize: 16 } }}>
      <Typography variant="h6">{t('hotelCostMarkup.title')}</Typography>
      <Typography>{t('hotelCostMarkup.description')}</Typography>
      {(error || settings.isError || admins.isError) && <Alert severity="error">{t('hotelCostMarkup.error')}</Alert>}
      {saved && <Alert severity="success">{t('hotelCostMarkup.saved')}</Alert>}
      <TextField
        type="number"
        label={t('hotelCostMarkup.default')}
        value={Number.isFinite(value.policy.defaultBasisPoints) ? value.policy.defaultBasisPoints / 100 : ''}
        disabled={busy}
        inputProps={{ min: 0, max: 99.99, step: 0.01 }}
        onChange={(e) => update({ ...value.policy, defaultBasisPoints: percentage(e.target.value) })}
      />
      {value.policy.overrides.map((entry, index) => (
        <Stack key={index} direction={{ xs: 'column', sm: 'row' }} spacing={1}>
          <TextField
            select
            fullWidth
            label={t('hotelCostMarkup.salesAgency')}
            value={entry.agencyId}
            disabled={busy}
            onChange={(e) =>
              update({
                ...value.policy,
                overrides: value.policy.overrides.map((x, i) =>
                  i === index ? { ...x, agencyId: Number(e.target.value) } : x,
                ),
              })
            }
          >
            {value.agencies.map((agency) => (
              <MenuItem key={agency.id} value={agency.id}>
                {agency.name}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            type="number"
            label={t('hotelCostMarkup.percent')}
            value={Number.isFinite(entry.basisPoints) ? entry.basisPoints / 100 : ''}
            disabled={busy}
            inputProps={{ min: 0, max: 99.99, step: 0.01 }}
            onChange={(e) =>
              update({
                ...value.policy,
                overrides: value.policy.overrides.map((x, i) =>
                  i === index ? { ...x, basisPoints: percentage(e.target.value) } : x,
                ),
              })
            }
          />
          <Button
            disabled={busy}
            onClick={() => update({ ...value.policy, overrides: value.policy.overrides.filter((_, i) => i !== index) })}
          >
            {t('hotelCostMarkup.remove')}
          </Button>
        </Stack>
      ))}
      <Box>
        <Button
          disabled={busy || value.policy.overrides.length >= value.agencies.length}
          onClick={() => {
            const next = value.agencies.find((a) => !value.policy.overrides.some((x) => x.agencyId === a.id));
            if (next)
              update({
                ...value.policy,
                overrides: [...value.policy.overrides, { agencyId: next.id, basisPoints: 0 }],
              });
          }}
        >
          {t('hotelCostMarkup.add')}
        </Button>
      </Box>
      <Stack direction="row" spacing={1}>
        <Button
          variant="contained"
          disabled={busy || !valid}
          onClick={() =>
            run(async () => {
              await client.put(path, { revision: value.revision, policy: value.policy });
              setDraft(undefined);
              await settings.refetch();
            })
          }
        >
          {t('hotelCostMarkup.save')}
        </Button>
        <Button
          disabled={busy}
          onClick={() => {
            setDraft(undefined);
            setError(false);
            void settings.refetch();
          }}
        >
          {t('hotelCostMarkup.reload')}
        </Button>
      </Stack>
      <Typography variant="h6">{t('hotelCostMarkup.admins')}</Typography>
      <Typography>{t('hotelCostMarkup.adminHint')}</Typography>
      {admins.data?.map((admin) => (
        <Stack key={admin.id} direction="row" spacing={2} alignItems="center">
          <Typography>{admin.email}</Typography>
          <Button disabled={busy || (admins.data?.length ?? 0) <= 1} onClick={() => role(admin.email, false)}>
            {t('hotelCostMarkup.revoke')}
          </Button>
        </Stack>
      ))}
      <TextField
        label={t('hotelCostMarkup.email')}
        type="email"
        value={email}
        disabled={busy}
        onChange={(e) => setEmail(e.target.value)}
      />
      <Box>
        <Button disabled={busy || !email.trim()} onClick={() => role(email, true)}>
          {t('hotelCostMarkup.grant')}
        </Button>
      </Box>
    </Stack>
  );
}
